/**
 * The case on a phone (scope §5.11): header, Riley's structured summary, the site, and the audience tabs as a swipe
 * strip (one panel per audience, native scroll-snap so a sideways swipe moves between them). Reply is a
 * deliberate second screen, opened from a panel. Owner: web-phone-pwa.
 */
import { automaticReplyCollapseLabel, Button, cn, ErrorBanner, MarkupText, MessageDirectionCue, Skeleton } from '@patchbay/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, ChevronDown, ChevronRight, ClipboardCheck, CornerUpLeft, FileText, MapPin, Sparkles } from 'lucide-react'
import * as React from 'react'
import { TicketBadge } from '../../components/ticket-ref'
import { formatWhen } from '../../lib/display-time'
import { mapsUrl } from '../../lib/maps'
import { CaseSummaryContent, MissingCaseSummary, structuredCaseSummary } from '../case-summary'
import { UpdatePanel } from '../case-update/update-panel'
import { meDeskQuery } from '../case-view/api'
import { PUBLIC_NOTE_LABEL } from '../case-view/public-note'
import { TechWorkflowPanel } from '../tech-workflow'
import {
  JumpToFirst,
  jumpToFirst,
  orderTurns,
  type TimelineOrderState,
  TimelineOrderToggle,
  turnEdge,
  useOpenOnNewest,
  useTimelineOrder,
} from '../timeline-order'
import {
  type Audience,
  buildCaseSummary,
  caseDetailQuery,
  describeError,
  type ThreadOut,
  type TurnOut,
  threadsQuery,
  turnsQuery,
} from './api'
import { formatDue, staleLabel } from './my-day-data'

const ORDER: Record<Audience, number> = { customer: 0, contractor: 1, vendor: 2, internal: 3, unclassified: 4 }

export function audienceLabel(t: ThreadOut): string {
  switch (t.audience) {
    case 'customer':
      return 'Customer'
    case 'contractor':
      return t.display_name_internal ?? 'Contractor'
    case 'vendor':
      return t.display_name_internal ?? 'Vendor'
    case 'internal':
      return 'Internal'
    default:
      return 'Unclassified'
  }
}

export function sortThreads(threads: ThreadOut[]): ThreadOut[] {
  return [...threads].sort((a, b) => ORDER[a.audience] - ORDER[b.audience] || audienceLabel(a).localeCompare(audienceLabel(b)))
}

/** Only customer and contractor/vendor threads take a reply; internal talk is a Desk private note. */
export function canReply(t: ThreadOut): boolean {
  return t.audience === 'customer' || t.audience === 'contractor' || t.audience === 'vendor'
}

export { mapsUrl }

export function MobileTurn({ turn, audience, edge }: { turn: TurnOut; audience: Audience; edge?: 'newest' | 'first' }) {
  const draft = turn.is_unsent_draft || turn.direction === 'draft'
  const publicNote = turn.direction === 'note' && audience === 'customer' && turn.audience_source === 'to_party'
  const sentDirection = draft
    ? null
    : publicNote
      ? 'outbound'
      : turn.direction === 'note'
        ? null
        : turn.direction === 'in' && turn.author_kind !== 'staff'
          ? 'inbound'
          : 'outbound'
  const directionLabel = sentDirection ? `${sentDirection === 'inbound' ? 'Inbound' : 'Outbound'} message` : null
  const autoReply = turn.is_auto_reply ? { label: 'Automatic reply', returnDate: turn.auto_reply_until ?? undefined } : null
  const autoReplyCollapsed = autoReply ? automaticReplyCollapseLabel(autoReply) : null
  const [autoReplyOpen, setAutoReplyOpen] = React.useState(false)
  const when = turn.sent_at ? formatWhen(turn.sent_at, { style: 'absolute' }) : null
  const who =
    turn.direction === 'note'
      ? publicNote
        ? PUBLIC_NOTE_LABEL
        : 'Private note'
      : turn.direction === 'out'
        ? `Sent${turn.author_kind === 'riley' ? ' by Riley' : ''}`
        : (turn.author_ref ?? turn.from_addr ?? turn.author_kind)
  const directionalFrame =
    sentDirection === 'inbound'
      ? 'mr-5 border-l-4 border-l-primary/60'
      : sentDirection === 'outbound'
        ? 'ml-5 border-r-4 border-r-success/60 bg-success-soft/15'
        : null
  if (autoReplyCollapsed && !autoReplyOpen) {
    return (
      <li data-turn-edge={edge} data-auto-reply="collapsed" className={cn('rounded-lg border border-border bg-muted/40', directionalFrame)}>
        <button
          type="button"
          aria-expanded={false}
          aria-label={`Expand ${autoReplyCollapsed.replace(/^Collapsed: /, '')} from ${who}${directionLabel ? `. ${directionLabel}` : ''}`}
          onClick={() => setAutoReplyOpen(true)}
          className="flex w-full min-w-0 cursor-pointer items-start gap-2 px-3 py-2 text-left"
        >
          <ChevronRight className="size-4 shrink-0 text-subtle" aria-hidden="true" />
          {sentDirection ? <MessageDirectionCue direction={sentDirection} focusable={false} /> : null}
          <span className="min-w-0 flex-1 text-sm leading-snug font-medium text-muted-foreground">{autoReplyCollapsed}</span>
          {when ? <time className="shrink-0 text-xs text-subtle">{when}</time> : null}
        </button>
      </li>
    )
  }
  return (
    <li
      data-turn-edge={edge}
      className={cn(
        'rounded-lg border p-3 text-base',
        draft ? 'border-dashed border-border-strong bg-muted/40' : 'border-border bg-card',
        turn.direction === 'note' && !publicNote && 'bg-warning-soft/40',
        directionalFrame,
      )}
    >
      <p className="mb-1 flex items-center gap-2 text-xs text-muted-foreground">
        {sentDirection ? <MessageDirectionCue direction={sentDirection} /> : null}
        {draft ? (
          <span className="font-semibold text-foreground">[DRAFT - not sent]</span>
        ) : autoReplyCollapsed ? (
          <span className="font-medium">{autoReplyCollapsed.replace(/^Collapsed: /, '')}</span>
        ) : (
          <span className="font-medium">{who}</span>
        )}
        {when && !draft ? <span className="ml-auto tabular">{when}</span> : null}
        {autoReplyCollapsed ? (
          <button
            type="button"
            aria-expanded={true}
            aria-label={`Collapse automatic reply from ${who}`}
            onClick={() => setAutoReplyOpen(false)}
            className="shrink-0 cursor-pointer rounded p-1 hover:bg-muted"
          >
            <ChevronDown className="size-4" aria-hidden="true" />
          </button>
        ) : null}
      </p>
      {turn.staff_delivery_note ? <p className="mb-1 text-xs font-medium text-warning">{turn.staff_delivery_note}</p> : null}
      <p className="line-clamp-6 whitespace-pre-wrap">
        <MarkupText text={turn.text_clean ?? ''} mentions={turn.mentions} />
      </p>
      {turn.attachments?.length ? <p className="mt-1 text-xs text-muted-foreground">{turn.attachments.length} attachment(s)</p> : null}
    </li>
  )
}

function Panel({
  thread,
  active,
  current,
  onReply,
  timeline,
}: {
  thread: ThreadOut
  active: boolean
  /** The panel on screen (active also covers its neighbours, prefetched for the swipe). */
  current: boolean
  onReply: (threadId: string) => void
  timeline: TimelineOrderState
}) {
  const q = useQuery({ ...turnsQuery(thread.id), enabled: active })
  const [all, setAll] = React.useState(false)
  const [jump, setJump] = React.useState(0)
  const ref = React.useRef<HTMLElement>(null)
  const { order } = timeline
  const turns = q.data?.data.turns ?? []
  // The newest five either way (#207); newest first lists them from the top, oldest first keeps them chronological.
  const count = all ? turns.length : Math.min(5, turns.length)
  const shown = order === 'newest' ? orderTurns(turns, 'newest').slice(0, count) : turns.slice(turns.length - count)
  const hidden = turns.length - count
  // The oldest message is only "first" when it is on screen; the newest is always shown.
  const edgeOf = (i: number) => {
    const edge = turnEdge(i, shown.length, order)
    return edge === 'first' && hidden > 0 ? undefined : edge
  }
  const stale = staleLabel(q.data?.servedAt ?? null)
  React.useEffect(() => {
    if (jump > 0) jumpToFirst(ref.current)
  }, [jump])
  useOpenOnNewest(ref, { openKey: thread.id, order, ready: current && turns.length > 0 })
  const reply = canReply(thread) ? (
    <Button variant="outline" className="h-12 w-full text-base" onClick={() => onReply(thread.id)}>
      <CornerUpLeft className="size-4" /> Reply to {audienceLabel(thread)}
    </Button>
  ) : null
  const older =
    hidden > 0 ? (
      <Button variant="ghost" className="h-11 w-full" onClick={() => setAll(true)}>
        Show {hidden} older
      </Button>
    ) : null
  return (
    <section
      ref={ref}
      role="tabpanel"
      id={`panel-${thread.id}`}
      aria-label={audienceLabel(thread)}
      className="w-full shrink-0 snap-start snap-always space-y-2 px-4 pt-2 pb-4"
    >
      {order === 'newest' ? reply : null}
      {turns.length > 0 ? (
        <div className="flex items-center justify-between gap-2">
          <TimelineOrderToggle order={order} onToggle={timeline.toggle} className="h-9" />
          {turns.length > 1 ? (
            <JumpToFirst
              order={order}
              onJump={() => {
                setAll(true)
                setJump((n) => n + 1)
              }}
            />
          ) : null}
        </div>
      ) : null}
      {q.data?.data.as_of ? (
        <p className="text-xs text-warning">Desk unreachable; mirror copy from {formatWhen(q.data.data.as_of, { style: 'absolute' })}.</p>
      ) : null}
      {stale ? <p className="text-xs text-warning">{stale}</p> : null}
      {q.isError ? (
        <ErrorBanner title="Conversation unreachable" detail={`${describeError(q.error)} This is not the same as no messages.`} />
      ) : q.isPending && active ? (
        <Skeleton className="h-24 w-full" />
      ) : q.data && turns.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">No messages in this conversation yet.</p>
      ) : (
        <>
          {order === 'oldest' ? older : null}
          <ol className="space-y-2">
            {shown.map((t, i) => (
              <MobileTurn key={t.id} turn={t} audience={thread.audience} edge={edgeOf(i)} />
            ))}
          </ol>
          {order === 'newest' ? older : null}
        </>
      )}
      {order === 'oldest' ? reply : null}
    </section>
  )
}

/** Tabs above, swipeable panels below. Tapping a tab scrolls to its panel; swiping updates the tab. */
export function AudienceStrip({ threads, onReply }: { threads: ThreadOut[]; onReply: (threadId: string) => void }) {
  const sorted = React.useMemo(() => sortThreads(threads), [threads])
  const timeline = useTimelineOrder()
  const [index, setIndex] = React.useState(0)
  const scroller = React.useRef<HTMLDivElement>(null)
  const go = (i: number) => {
    setIndex(i)
    const el = scroller.current
    if (el && typeof el.scrollTo === 'function') el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' })
  }
  const onScroll = () => {
    const el = scroller.current
    if (!el || el.clientWidth === 0) return
    const i = Math.round(el.scrollLeft / el.clientWidth)
    if (i !== index && i >= 0 && i < sorted.length) setIndex(i)
  }
  if (sorted.length === 0) return <p className="px-4 py-6 text-center text-sm text-muted-foreground">No conversations on this case yet.</p>
  return (
    <div>
      <div role="tablist" aria-label="Audiences" className="flex gap-1.5 overflow-x-auto px-4 pb-2 [scrollbar-width:none]">
        {sorted.map((t, i) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={i === index}
            aria-controls={`panel-${t.id}`}
            onClick={() => go(i)}
            className={cn(
              'h-10 shrink-0 cursor-pointer rounded-full border px-4 text-sm font-medium',
              i === index ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card text-foreground',
            )}
          >
            {audienceLabel(t)}
            {t.unread ? <span className="ml-1.5 tabular text-xs opacity-80">{t.unread}</span> : null}
          </button>
        ))}
      </div>
      <div
        ref={scroller}
        onScroll={onScroll}
        data-testid="audience-panels"
        className="flex snap-x snap-mandatory overflow-x-auto overscroll-x-contain [scrollbar-width:none]"
      >
        {sorted.map((t, i) => (
          <Panel key={t.id} thread={t} active={Math.abs(i - index) <= 1} current={i === index} onReply={onReply} timeline={timeline} />
        ))}
      </div>
    </div>
  )
}

export function PhoneCase({ caseId, onBack, onReply }: { caseId: string; onBack: () => void; onReply: (threadId: string) => void }) {
  const c = useQuery(caseDetailQuery(caseId))
  const th = useQuery(threadsQuery(caseId))
  const detail = c.data?.data
  const stale = staleLabel(c.data?.servedAt ?? null)
  const [updating, setUpdating] = React.useState(false)
  const me = useQuery(meDeskQuery())
  const queryClient = useQueryClient()
  const summary = structuredCaseSummary(detail?.case_summary)
  const buildSummary = async () => {
    await buildCaseSummary(caseId)
    await Promise.all([c.refetch(), queryClient.invalidateQueries({ queryKey: ['my-day'] })])
  }
  return (
    <div className="pb-4">
      <div className="flex items-center gap-2 px-2 py-2">
        <Button variant="ghost" className="h-11" onClick={onBack}>
          <ArrowLeft className="size-5" /> My day
        </Button>
      </div>
      {c.isError ? (
        <div className="px-4">
          <ErrorBanner title="Case unreachable" detail={describeError(c.error)} onRetry={() => void c.refetch()} />
        </div>
      ) : !detail ? (
        <div className="space-y-2 px-4">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : (
        <div className="space-y-3 px-4 pb-3">
          <div>
            <p className="tabular text-sm text-muted-foreground">{detail.number}</p>
            <h1 className="text-2xl font-bold">{detail.customer_display ?? 'Customer not set'}</h1>
            <p className="text-base">{detail.title}</p>
            {stale ? <p className="text-xs text-warning">{stale}</p> : null}
          </div>
          {detail.next_move ? (
            <p
              className={cn(
                'rounded-lg border px-3 py-2 text-base',
                detail.next_move.overdue ? 'border-destructive/40 bg-destructive-soft' : 'border-border bg-card',
              )}
            >
              <span className="block text-xs text-muted-foreground">
                Next move · {detail.next_move.overdue ? 'overdue · ' : ''}
                {formatDue(detail.next_move.due_at ? new Date(detail.next_move.due_at) : null)}
              </span>
              {detail.next_move.text}
            </p>
          ) : (
            <p className="rounded-lg border border-dashed border-border px-3 py-2 text-sm text-muted-foreground">No next move set.</p>
          )}
          <TechWorkflowPanel caseId={caseId} caseNumber={detail.number} summary={detail.summary_line} site={detail.site_address} compact />
          {me.data?.role !== 'billing' ? (
            <Button variant="primary" className="h-12 w-full text-base" onClick={() => setUpdating(true)}>
              <ClipboardCheck className="size-5" /> Update
            </Button>
          ) : null}
          <div className="rounded-lg border border-riley-border bg-riley-soft/50 p-3">
            <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-riley">
              <Sparkles className="size-3.5" /> Riley's summary
            </p>
            {summary ? <CaseSummaryContent summary={summary} /> : <MissingCaseSummary onBuild={buildSummary} />}
          </div>
          {detail.site_address ? (
            <a
              href={mapsUrl(detail.site_address)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-3 text-base"
            >
              <MapPin className="size-5 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate">{detail.site_address}</span>
              <span className="text-sm text-primary">Maps</span>
            </a>
          ) : null}
          {detail.tickets.length ? (
            <p className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
              <FileText className="size-3.5" />
              {detail.tickets.map((t) =>
                t.tombstoned ? (
                  <span key={t.desk_ticket_id} className={cn('tabular', 'line-through')}>
                    #{t.ticket_number}
                  </span>
                ) : (
                  <TicketBadge key={t.desk_ticket_id} number={t.ticket_number} caseNumber={detail.number} deskTicketId={t.desk_ticket_id} />
                ),
              )}
            </p>
          ) : null}
        </div>
      )}
      {th.isError ? (
        <div className="px-4">
          <ErrorBanner title="Conversations unreachable" detail={describeError(th.error)} onRetry={() => void th.refetch()} />
        </div>
      ) : th.data ? (
        <AudienceStrip threads={th.data.data} onReply={onReply} />
      ) : (
        <div className="px-4">
          <Skeleton className="h-10 w-full" />
        </div>
      )}
      {detail ? (
        <UpdatePanel
          open={updating}
          onClose={() => setUpdating(false)}
          caseUuid={caseId}
          caseNumber={detail.number}
          onUpdated={() => void c.refetch()}
        />
      ) : null}
    </div>
  )
}
